import { useFormik } from 'formik';
// mantine
import {
    Button,
    NumberInput,
    Radio,
    TextInput,
    UnstyledButton
} from '@mantine/core';
// utils
import { useQueryClient } from '@tanstack/react-query';
import type { ILLMKey, ILLMKeyData } from '@newo/api/gateway';
import { ChangeEvent } from 'react';
import {
    QUERY_KEYS,
    useCreateKeyMutation,
    useUpdateKeyMutation
} from '@/services/customer/providers/providers.queries.ts';
import validationSchema from '@/pages/Profile/components/LLMKeys/components/LLMKeyForm/LLMKeyForm.schema.ts';

interface LLMKeyFormProps {
    providerId: string;
    hasMainKey?: boolean;
    llmKey?: ILLMKey;
    onClose?: () => void;
}

const LLMKeyForm = ({
    llmKey,
    onClose,
    providerId,
    hasMainKey = false
}: LLMKeyFormProps) => {
    const queryClient = useQueryClient();
    const createMutation = useCreateKeyMutation();
    const updateMutation = useUpdateKeyMutation();
    const initialValues: ILLMKeyData = {
        key: llmKey?.key ?? '',
        role: llmKey?.role ?? 'main',
        priority: llmKey?.priority ?? 1
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit(data) {
            if (llmKey) {
                updateMutation.mutate(
                    { keyId: llmKey.id, data },
                    {
                        onSuccess: () => {
                            queryClient.invalidateQueries({
                                queryKey:
                                    QUERY_KEYS.getLLMProviderKeys(providerId)
                            });
                            onClose?.();
                        }
                    }
                );
            } else {
                createMutation.mutate(
                    { providerId, data },
                    {
                        onSuccess: () => {
                            queryClient.invalidateQueries({
                                queryKey:
                                    QUERY_KEYS.getLLMProviderKeys(providerId)
                            });
                            queryClient.invalidateQueries({
                                queryKey: ['allKeys']
                            });

                            onClose?.();
                        }
                    }
                );
            }
        }
    });

    const handleCancel = () => {
        onClose?.();
    };

    const handleKeyChange = (event: ChangeEvent<HTMLInputElement>) => {
        formik.setFieldValue('key', event.target.value.trim());
    };

    const handleRoleChange = (value: string) => {
        formik.setFieldValue('role', value);
    };

    const handlePriorityChange = (value: number | string) => {
        formik.setFieldValue('priority', value);
    };

    return (
        <form
            autoComplete="off"
            className="flex flex-col gap-3"
            onSubmit={formik.handleSubmit}
        >
            <TextInput
                error={
                    formik.touched.key && formik.values.key
                        ? formik.errors.key
                        : undefined
                }
                label="LLM Key"
                name="key"
                placeholder="LLM Key"
                value={formik.values.key}
                onBlur={formik.handleBlur}
                onChange={handleKeyChange}
            />

            <Radio.Group
                aria-disabled={!hasMainKey}
                label="Role"
                name="role"
                value={formik.values.role}
                onChange={handleRoleChange}
            >
                <div className="flex gap-4">
                    <Radio label="Main" value="main" variant="outline" />
                    <Radio
                        disabled={!hasMainKey}
                        label="Support"
                        value="support"
                        variant="outline"
                    />
                </div>
            </Radio.Group>

            <NumberInput
                error={
                    formik.touched.priority && formik.values.priority
                        ? formik.errors.priority
                        : undefined
                }
                label="Priority"
                max={100}
                min={1}
                name="priority"
                placeholder="Priority"
                value={formik.values.priority}
                onChange={handlePriorityChange}
            />

            <div className="flex justify-between gap-2">
                <UnstyledButton
                    className="text-xs text-gray-6"
                    onClick={handleCancel}
                >
                    Cancel
                </UnstyledButton>
                <Button
                    disabled={
                        !formik.dirty || !formik.isValid || formik.isSubmitting
                    }
                    size="xs"
                    type="submit"
                >
                    {llmKey ? 'Update' : 'Create'}
                </Button>
            </div>
        </form>
    );
};

export default LLMKeyForm;
